import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Box from '../../shared/components/Box';

interface IVolumetricToolConfigProps {
  isVisible: boolean;
  onConfigChange: (config: VolumetricConfig) => void;
  onClose?: () => void;
  initialConfig?: VolumetricConfig;
  onRecalculate?: () => void;
  densityUnit?: string;
  lengthUnit?: string;
}

export interface VolumetricConfig {
  density: number;
  heightLimit: number;
  showHeightLimitBox?: boolean;
}

const ConfigPanel = styled(Box)`
  flex-direction: column;
  gap: 15px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  border: 1px solid #ddd;
  margin-top: 10px;
`;

const ConfigRow = styled(Box)`
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 13px;
`;

const Input = styled.input`
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const ConfigTitle = styled.h4`
  margin: 0 0 10px 0;
  color: #333;
  font-size: 14px;
`;

const ConfigHeader = styled(Box)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  padding: 0;
  
  &:hover {
    color: #333;
  }
`;

const RecalculateButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  margin-top: 15px;
  width: 100%;
  
  &:hover {
    background: #0056b3;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const CheckboxRow = styled(Box)`
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  margin: 0;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
`;

const VolumetricToolConfig: React.FC<IVolumetricToolConfigProps> = ({
  isVisible,
  onConfigChange,
  onClose,
  initialConfig = { density: 80.0, heightLimit: 0, showHeightLimitBox: true },
  onRecalculate,
  densityUnit = "lbs/ft³",
  lengthUnit = "m"
}) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<VolumetricConfig>(initialConfig);

  useEffect(() => {
    setConfig(initialConfig);
  }, [initialConfig]);

  const handleDensityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0) {
      const newConfig = { ...config, density: value };
      setConfig(newConfig);
      // Don't call onConfigChange immediately - only on button click
    }
  };

  const handleHeightLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0) {
      const newConfig = { ...config, heightLimit: value };
      setConfig(newConfig);
      // Don't call onConfigChange immediately - only on button click
    }
  };

  const handleShowBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newConfig = { ...config, showHeightLimitBox: e.target.checked };
    setConfig(newConfig);
    // Apply this change immediately since it's just a visibility toggle
    onConfigChange(newConfig);
  };

  const handleRecalculate = () => {
    // Apply the current config and then recalculate
    onConfigChange(config);
    if (onRecalculate) {
      onRecalculate();
    }
  };

  if (!isVisible) return null;

  return (
    <ConfigPanel>
      <ConfigHeader>
        <ConfigTitle>Volume Configuration</ConfigTitle>
        {onClose && (
          <CloseButton onClick={onClose} title="Close">
            ×
          </CloseButton>
        )}
      </ConfigHeader>
      
      <ConfigRow>
        <Label htmlFor="density">
          Density ({densityUnit})
        </Label>
        <Input
          id="density"
          type="number"
          step="0.1"
          min="0"
          value={config.density}
          onChange={handleDensityChange}
          placeholder="1.0"
          onKeyDown={(e) => {
            // Allow: backspace, delete, tab, escape, enter, period
            if ([8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
                // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
                (e.keyCode === 65 && e.ctrlKey === true) ||
                (e.keyCode === 67 && e.ctrlKey === true) ||
                (e.keyCode === 86 && e.ctrlKey === true) ||
                (e.keyCode === 88 && e.ctrlKey === true) ||
                (e.keyCode === 90 && e.ctrlKey === true) ||
                // Allow: home, end, left, right, down, up
                (e.keyCode >= 35 && e.keyCode <= 40)) {
              return; // let it happen, don't do anything
            }
            // Ensure that it is a number and stop the keypress
            if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
              e.preventDefault();
            }
          }}
        />
      </ConfigRow>

      <ConfigRow>
        <Label htmlFor="heightLimit">
          Height Limit ({lengthUnit})
        </Label>
        <Input
          id="heightLimit"
          type="number"
          step="0.1"
          min="0"
          value={config.heightLimit}
          onChange={handleHeightLimitChange}
          placeholder="0"
          onKeyDown={(e) => {
            // Allow: backspace, delete, tab, escape, enter, period
            if ([8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
                // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
                (e.keyCode === 65 && e.ctrlKey === true) ||
                (e.keyCode === 67 && e.ctrlKey === true) ||
                (e.keyCode === 86 && e.ctrlKey === true) ||
                (e.keyCode === 88 && e.ctrlKey === true) ||
                (e.keyCode === 90 && e.ctrlKey === true) ||
                // Allow: home, end, left, right, down, up
                (e.keyCode >= 35 && e.keyCode <= 40)) {
              return; // let it happen, don't do anything
            }
            // Ensure that it is a number and stop the keypress
            if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
              e.preventDefault();
            }
          }}
        />
      </ConfigRow>
      
      <CheckboxRow>
        <Checkbox
          id="showHeightLimitBox"
          type="checkbox"
          checked={config.showHeightLimitBox ?? true}
          onChange={handleShowBoxChange}
        />
        <CheckboxLabel htmlFor="showHeightLimitBox">
          Show Box
        </CheckboxLabel>
      </CheckboxRow>
      
      <RecalculateButton onClick={handleRecalculate}>
        Recalculate Volume
      </RecalculateButton>
    </ConfigPanel>
  );
};

export default VolumetricToolConfig;